import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Gift, Copy, Users, Euro, Repeat } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';

export default function Affiliation() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/affiliation').then(({ data }) => setData(data)).catch(e => toast.error(apiError(e, "Échec du chargement de l'affiliation")));
  }, []);

  const copy = () => { navigator.clipboard?.writeText(data?.code || ''); toast.success('Code de parrainage copié'); };
  const shareLink = data?.code ? `https://chat-hubs.com/?ref=${data.code}` : '';
  const copyLink = () => { navigator.clipboard?.writeText(shareLink); toast.success('Lien de parrainage copié'); };
  const taux = Math.round((data?.commission_rate ?? 0.20) * 100);
  // 25/09 : un compte sans abonnement payant est réglé par virement — ne pas lui
  // promettre un crédit sur un abonnement qu'il n'a pas.
  const enCredit = data?.payout_mode === 'credit';
  const suivant = data?.palier_suivant || null;
  const euros = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto fade-in" data-testid="affiliation-page">
      <PageHeader title="Affiliation" subtitle="Parraine des agences et touche une commission sur chacun de leurs paiements, tous les mois" />

      <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 to-violet-500/5 p-6 mb-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-500/20 flex items-center justify-center"><Gift size={20} className="text-indigo-300" /></div>
          <div>
            <div className="text-base font-bold text-white">Ton code de parrainage</div>
            <div className="text-xs text-slate-400">
              Tu es à <span className="text-white font-medium">{taux}% de chaque paiement</span> des agences que tu amènes, tant qu'elles restent clientes.
              {suivant && <> Encore <span className="text-emerald-300 font-medium">{suivant.manque} client{suivant.manque > 1 ? 's' : ''} qui paie{suivant.manque > 1 ? 'nt' : ''}</span> et tu passes à <span className="text-emerald-300 font-medium">{Math.round(suivant.taux * 100)}%</span> — sur tous tes clients, pas seulement les suivants.</>}
            </div>
          </div>
        </div>

        {/* Les paliers : on voit d'un coup d'oeil ou on en est et ce qu'il reste a faire. */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="affiliation-paliers">
          {(data?.paliers || []).map((p) => {
            const atteint = (data?.payants ?? 0) >= p.a_partir_de;
            const courant = Math.round(p.taux * 100) === taux;
            return (
              <div key={p.a_partir_de}
                className={`rounded-xl border px-3 py-2 text-xs ${courant
                  ? 'border-emerald-400/50 bg-emerald-500/10 text-emerald-200'
                  : atteint ? 'border-white/10 bg-white/[0.04] text-slate-300' : 'border-white/10 bg-white/[0.02] text-slate-500'}`}>
                <span className="font-semibold">{Math.round(p.taux * 100)}%</span>
                {' '}· {p.a_partir_de === 0 ? 'dès le 1er client' : `à partir de ${p.a_partir_de} clients`}
                {courant && ' · toi'}
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 max-w-md">
          <Input data-testid="referral-code-input" value={data?.code || '...'} readOnly className="bg-black/30 border-white/10 text-white font-mono h-11 text-base" />
          <Button data-testid="copy-referral-button" className="bg-indigo-500 hover:bg-indigo-600 h-11" onClick={copy}><Copy size={15} className="mr-1.5" /> Copier</Button>
        </div>
        <div className="mt-3 max-w-xl">
          <div className="text-xs text-slate-400 mb-1.5">Ton lien de parrainage — toute personne qui s'inscrit via ce lien t'est automatiquement rattachée :</div>
          <div className="flex gap-2">
            <Input data-testid="referral-link-input" value={shareLink || '...'} readOnly className="bg-black/30 border-white/10 text-white font-mono h-11 text-sm" />
            <Button data-testid="copy-referral-link-button" variant="outline" className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 h-11" onClick={copyLink}><Copy size={15} className="mr-1.5" /> Copier le lien</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat icon={Users} label="Total parrainés" value={data?.total ?? 0} color="text-indigo-400" />
        <Stat icon={Users} label="Clients qui paient" value={data?.payants ?? 0} color="text-emerald-400" testid="affiliation-payants" />
        <Stat icon={Repeat} label="Revenus par mois" value={euros(data?.monthly_recurring)} color="text-cyan-400"
          testid="affiliation-recurrent" />
        <Stat icon={Euro} label={enCredit ? 'À créditer' : 'À te verser'} value={euros(data?.earned_pending)} color="text-amber-400" />
        <Stat icon={Euro} label="Total gagné" value={euros(data?.earned_total)} color="text-fuchsia-400" />
      </div>

      {/* Ce qu'il gagne, plan par plan — pour qu'il sache quoi dire en prospectant. */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden mb-5" data-testid="affiliation-bareme">
        <div className="px-5 py-3 border-b border-white/5 text-sm font-semibold text-white">Ce que tu gagnes, chaque mois, par agence amenée</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-5 py-2 font-medium">Son plan</th>
              <th className="px-5 py-2 font-medium">Elle paie</th>
              <th className="px-5 py-2 font-medium">Tu touches / mois</th>
              <th className="px-5 py-2 font-medium">Sur 12 mois</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {(data?.bareme || []).map((b) => (
              <tr key={b.plan}>
                <td className="px-5 py-2.5 text-white">{b.plan}</td>
                <td className="px-5 py-2.5 text-slate-400">{euros(b.price)} / mois</td>
                <td className="px-5 py-2.5 text-emerald-300 font-semibold">{euros(b.commission)}</td>
                <td className="px-5 py-2.5 text-slate-400">{euros(b.commission * 12)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-white/5 text-xs text-slate-400">
          {enCredit
            ? <>Tes gains sont <span className="text-emerald-300">déduits de ton propre abonnement</span> — rien à réclamer, rien à facturer : ta prochaine facture baisse d'autant.</>
            : <>Tes gains te sont <span className="text-emerald-300">versés directement</span> par virement. La commission tombe à chaque facture réellement encaissée.</>}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 text-sm font-semibold text-white">Historique des parrainages</div>
        {(data?.history || []).length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Aucun parrainage pour l'instant. Partage ton lien pour commencer à toucher des commissions.</div>
        ) : (
          <table className="w-full">
            <tbody className="divide-y divide-white/5">
              {data.history.map((h, i) => (
                <tr key={h.id || `${h.agency}-${i}`}>
                  <td className="px-5 py-3 text-sm text-white">{h.agency}</td>
                  <td className="px-5 py-3 text-sm text-slate-400">{h.plan || '—'}</td>
                  <td className="px-5 py-3 text-sm text-slate-500">{h.joined_at ? new Date(h.joined_at).toLocaleDateString('fr-FR') : ''}</td>
                  <td className="px-5 py-3"><Badge>{h.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
      <Icon size={16} className={`${color} mb-2`} />
      <div className="text-xl font-bold text-white font-display">{value}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}
